<template>
    <div class="relative">
        <div class="mb-6 flex items-center justify-between">
            <div class="space-y-1">
                <ULink :to="`/field-posts/${postId}`" class="flex items-center space-x-1 text-sm font-semibold text-gray-500 hover:text-gray-700">
                    <span>{{ intervention?.post?.name ?? 'Field Post' }}</span>
                    <UIcon name="i-ph-caret-right-bold" class="size-2.5" />
                </ULink>
                <div v-if="intervention" class="flex items-center space-x-4">
                    <h1 class="text-3xl font-semibold text-gray-700">Intervention <span class="font-mono">#{{ number }}</span></h1>
                    <BadgeInterventionStatus :status="intervention.status" />
                </div>
            </div>

            <div v-if="intervention" class="flex items-center gap-x-2">
                <UButton v-if="hasErrors" color="white" icon="i-ph-arrow-clockwise-bold" label="Retry Save" @click="retryAll()" />
                <span v-else-if="isSaving" class="text-sm text-gray-400">Saving...</span>
                <span v-else-if="hasSaved" class="text-sm text-gray-400">All changes saved</span>
                <UButton color="orange" icon="i-ph-megaphone-bold" label="Feedback Report" @click="overlays.feedback = true" />
                <UDropdown v-if="canWrite" :items="actions" :popper="{ placement: 'bottom-end' }">
                    <UButton color="black" label="Actions" icon="i-ph-caret-down-bold" trailing />
                </UDropdown>
            </div>
        </div>

        <div v-if="intervention" class="relative flex items-start gap-4">
            <div class="sticky top-0 w-96 flex-shrink-0 space-y-4">
                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center justify-between px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Patient</h3>
                        <UButton v-if="canWrite && patient?.source === 'field_post'" color="white" size="xs" icon="i-ph-pencil-simple-bold" label="Edit"
                            @click="overlays.editPatient = true" />
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <Field label="Name">
                            <span v-if="patientName">{{ patientName }}</span>
                            <span v-else class="text-gray-300">–</span>
                        </Field>
                        <Field label="Phone">
                            <PhoneLabel v-if="patient?.phone" :value="patient.phone" />
                            <span v-else class="text-gray-300">–</span>
                        </Field>
                        <Field label="Date of Birth">
                            <span v-if="patient?.birth_date">
                                {{ dayjs(patient.birth_date).format('MMM D, YYYY') }}
                                <span class="text-gray-500">({{ age(patient.birth_date) }})</span>
                            </span>
                            <span v-else class="text-gray-300">–</span>
                        </Field>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Gender">
                                <span v-if="patient?.gender" class="capitalize">{{ patient.gender }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                            <Field label="Blood Type">
                                <span v-if="patient?.blood_type">{{ patient.blood_type }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                        </div>
                        <Field label="Allergies">
                            <span v-if="patient?.allergies" class="whitespace-pre-line">{{ patient.allergies }}</span>
                            <span v-else class="text-gray-300">–</span>
                        </Field>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Details</h3>
                    </div>
                    <div class="px-6 py-5 space-y-4">
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Post">
                                <ULink :to="`/field-posts/${postId}`" class="hover:underline">{{ intervention.post?.name }}</ULink>
                            </Field>
                            <Field label="Area">
                                <span v-if="form.area">{{ form.area }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Arrived">
                                <span>{{ dayjs(intervention.arrived_at).format('MMM D, YYYY HH:mm') }}</span>
                            </Field>
                            <Field label="Discharged">
                                <span v-if="form.discharged_local">{{ dayjs(form.discharged_local).format('MMM D, YYYY HH:mm') }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <Field label="Nurse">
                                <span v-if="form.nurse_id">{{ nameOf(form.nurse_id) }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                            <Field label="Physician">
                                <span v-if="form.physician_id">{{ nameOf(form.physician_id) }}</span>
                                <span v-else class="text-gray-300">–</span>
                            </Field>
                        </div>
                        <Field label="Created By">
                            <span v-if="names[intervention.created_by]">{{ names[intervention.created_by] }}</span>
                            <span v-else class="text-gray-300">–</span>
                        </Field>
                    </div>
                </div>
            </div>

            <div class="flex-1 space-y-4">
                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">History & Reason</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <UFormGroup label="Area" :error="saveError('area')">
                            <USelectMenu v-model="form.area" :options="intervention.post?.areas ?? []"  placeholder="Select..."
                                :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pick('area', v)">
                                <template v-if="canWrite && form.area" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="pick('area', null)">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                        <UFormGroup label="Reason" :error="saveError('reason_id')">
                            <USelectMenu v-model="form.reason_id" :options="reasons" value-attribute="id" option-attribute="label" searchable placeholder="Select..."
                                :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pick('reason_id', v)">
                                <template v-if="canWrite && form.reason_id" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="pick('reason_id', null)">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                        <UFormGroup label="Known Allergy" :error="saveError('has_allergy')">
                            <USelectMenu v-model="allergyChoice" :options="yesNo" value-attribute="value" option-attribute="label" placeholder="Unknown"
                                :disabled="!canWrite" :ui="CLEARABLE_UI">
                                <template v-if="canWrite && allergyChoice" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="allergyChoice = null">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                        <UFormGroup v-if="form.has_allergy" label="Allergy Details" :error="saveError('allergy_details')">
                            <UInput v-model="form.allergy_details" :disabled="!canWrite" @blur="saveText('allergy_details')" />
                        </UFormGroup>
                        <UFormGroup label="Circumstances" class="col-span-2" :error="saveError('circumstances')">
                            <UTextarea v-model="form.circumstances" :rows="3" autoresize :disabled="!canWrite" @blur="saveText('circumstances')" />
                        </UFormGroup>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Initial Vitals</h3>
                    </div>
                    <div class="px-6 py-5">
                        <FieldPostVitalsGrid :model-value="form.vitals_initial" :disabled="!canWrite" @commit="(v) => saveVitals('vitals_initial', v)" />
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Clinical Assessment</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <UFormGroup label="Clinical Examination" class="col-span-2" :error="saveError('clinical_exam')">
                            <UTextarea v-model="form.clinical_exam" :rows="4" autoresize :disabled="!canWrite" @blur="saveText('clinical_exam')" />
                        </UFormGroup>
                        <UFormGroup label="Special Notes and Precautions" class="col-span-2" :error="saveError('precautions')">
                            <UTextarea v-model="form.precautions" :rows="2" autoresize :disabled="!canWrite" @blur="saveText('precautions')" />
                        </UFormGroup>
                    </div>
                </div>

                <FieldPostTreatmentsSection :intervention-id="interventionId" :treatments="treatments" :team="team" :can-write="canWrite" />

                <FieldPostUsagesSection :intervention-id="interventionId" :post-id="postId" :can-write="canWrite" />

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Outcome</h3>
                    </div>
                    <div class="px-6 py-5 space-y-6">
                        <div class="grid grid-cols-2 gap-4">
                            <UFormGroup label="Disposition" :error="saveError('disposition_id')">
                                <USelectMenu v-model="form.disposition_id" :options="dispositions" value-attribute="id" option-attribute="label" searchable placeholder="Select..."
                                :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pick('disposition_id', v)">
                                <template v-if="canWrite && form.disposition_id" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="pick('disposition_id', null)">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                            </UFormGroup>
                            <UFormGroup label="Discharge Time" :error="saveError('discharged_at')">
                                <div class="flex gap-2">
                                    <UInput v-model="form.discharged_local" type="datetime-local" class="flex-1" :disabled="!canWrite"
                                        @blur="saveDischarge()" />
                                    <UButton v-if="canWrite" color="white" label="Now" @click="dischargeNow()" />
                                </div>
                            </UFormGroup>
                            <UFormGroup label="Emergency Contact" :error="saveError('emergency_contact_name')">
                                <UInput v-model="form.emergency_contact_name" :disabled="!canWrite" @blur="saveText('emergency_contact_name')" />
                            </UFormGroup>
                            <UFormGroup label="Contact Phone" :error="saveError('emergency_contact_phone')">
                                <InputPhone v-model="form.emergency_contact_phone" size="md" :disabled="!canWrite"
                                    @change="saveText('emergency_contact_phone')" />
                            </UFormGroup>
                            <UFormGroup label="Progress Notes" class="col-span-2" :error="saveError('progress_notes')">
                                <UTextarea v-model="form.progress_notes" :rows="3" autoresize :disabled="!canWrite" @blur="saveText('progress_notes')" />
                            </UFormGroup>
                        </div>
                        <div class="space-y-4">
                            <h4 class="text-sm font-semibold text-gray-800">Post-Treatment Vitals</h4>
                            <FieldPostVitalsGrid :model-value="form.vitals_post" :keys="POST_VITAL_KEYS" :disabled="!canWrite"
                                @commit="(v) => saveVitals('vitals_post', v)" />
                        </div>
                    </div>
                </div>

                <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
                    <div class="flex items-center px-6 py-3 border-b border-gray-300">
                        <h3 class="text-lg text-gray-700 font-bold">Clinical Team</h3>
                    </div>
                    <div class="px-6 py-5 grid grid-cols-2 gap-4">
                        <UFormGroup label="Nurse" :error="saveError('nurse_id')">
                            <USelectMenu v-model="form.nurse_id" :options="team" value-attribute="id" option-attribute="name" searchable placeholder="Select..."
                                :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pick('nurse_id', v)">
                                <template v-if="canWrite && form.nurse_id" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="pick('nurse_id', null)">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                        <UFormGroup label="Physician" :error="saveError('physician_id')">
                            <USelectMenu v-model="form.physician_id" :options="team" value-attribute="id" option-attribute="name" searchable placeholder="Select..."
                                :disabled="!canWrite" :ui="CLEARABLE_UI" @change="(v: string) => pick('physician_id', v)">
                                <template v-if="canWrite && form.physician_id" #trailing>
                                    <button type="button" class="flex items-center justify-center" @click.stop="pick('physician_id', null)">
                                        <UIcon name="i-ph-x-bold" class="size-3" />
                                    </button>
                                </template>
                            </USelectMenu>
                        </UFormGroup>
                    </div>
                </div>

                <FieldPostReportsSection ref="reportsSection" :intervention-id="interventionId" />
            </div>
        </div>

        <OverlayFieldPostEditPatient v-model:open="overlays.editPatient" :intervention-id="interventionId" :patient="patient"
            @saved="onPatientSaved" />
        <OverlayFieldPostCreateFeedbackReport v-model:open="overlays.feedback" :intervention-id="interventionId"
            @reload="reportsSection?.fetchReports()" />
        <OverlayFieldPostCompleteIntervention v-model:open="statusChange.open" :status="statusChange.status" :missing="statusChange.missing"
            :loading="statusChange.loading" @confirm="confirmStatus()" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { OptionNamespace, type Option } from '~/types/option'
import {
    FIELD_POST_READ_ROLES, FIELD_POST_WRITE_ROLES, INTERVENTION_STATUS_META, InterventionStatus, MISSING_LABELS, POST_VITAL_KEYS,
    type FieldPostPatient, type Intervention, type TeamMember, type Vitals,
} from '~/types/fieldPost'
import { fromLocalInput, missingForCompletion, toLocalInput } from '~/utils/fieldPosts'

definePageMeta({ layout: 'default', middleware: 'auth', roles: FIELD_POST_READ_ROLES })

const route = useRoute()
const client = useSupabaseClient()
const auth = useAuthStore()
const toast = useToast()
const { age } = useNumber()

const postId = route.params.id as string
const interventionId = route.params.iid as string

const intervention = ref<Intervention>()
const patient = ref<FieldPostPatient>()
const options = ref<Option[]>([])
const team = ref<TeamMember[]>([])
const names = ref<Record<string, string>>({})
const reportsSection = ref<{ fetchReports: () => Promise<void> }>()
const overlays = reactive({ editPatient: false, feedback: false })
const statusChange = reactive({
    open: false,
    loading: false,
    status: InterventionStatus.completed as InterventionStatus,
    missing: [] as string[],
})

const canWrite = computed(() => !!auth.hasRole(...FIELD_POST_WRITE_ROLES))
const reasons = computed(() => options.value.filter(o => o.namespace === OptionNamespace.interventionReason))
const dispositions = computed(() => options.value.filter(o => o.namespace === OptionNamespace.interventionDisposition))
const treatments = computed(() => options.value.filter(o => o.namespace === OptionNamespace.treatment))
const number = computed(() => String(intervention.value?.number ?? '').padStart(6, '0'))
const patientName = computed(() => [patient.value?.first_name, patient.value?.last_name].filter(Boolean).join(' '))

const CLEARABLE_UI = { icon: { trailing: { pointer: 'pointer-events-auto' } } }

const yesNo = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
]

const FIELD_LABELS: Record<string, string> = {
    area: 'Area',
    reason_id: 'Reason',
    has_allergy: 'Known Allergy',
    allergy_details: 'Allergy Details',
    circumstances: 'Circumstances',
    vitals_initial: 'Initial Vitals',
    vitals_post: 'Post-Treatment Vitals',
    clinical_exam: 'Clinical Examination',
    precautions: 'Special Notes and Precautions',
    disposition_id: 'Disposition',
    discharged_at: 'Discharge Time',
    progress_notes: 'Progress Notes',
    emergency_contact_name: 'Emergency Contact',
    emergency_contact_phone: 'Contact Phone',
    nurse_id: 'Nurse',
    physician_id: 'Physician',
}

const form = reactive({
    area: null as string | null,
    reason_id: null as string | null,
    has_allergy: null as boolean | null,
    allergy_details: '',
    circumstances: '',
    vitals_initial: {} as Vitals,
    vitals_post: {} as Vitals,
    clinical_exam: '',
    precautions: '',
    disposition_id: null as string | null,
    discharged_local: '',
    progress_notes: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    nurse_id: null as string | null,
    physician_id: null as string | null,
})

const { states, saveField, retry, retryAll, flush, hasErrors, isSaving, hasSaved } = useAutosave(async (patch) => {
    const { error } = await client.from('interventions').update(patch as never).eq('id', interventionId).select('id').single()
    if (error) throw error
}, (key, error) => {
    toast.add({
        title: `${FIELD_LABELS[key] ?? 'Change'} not saved.`,
        description: (error as { message?: string })?.message,
        color: 'red',
        icon: 'i-ph-x-circle-duotone',
        actions: [{ label: 'Retry', color: 'white', click: () => retry(key) }],
    })
})

const allergyChoice = computed({
    get: () => form.has_allergy === null ? null : form.has_allergy ? 'yes' : 'no',
    set: (value: string | null) => {
        const next = value === null ? null : value === 'yes'
        if (next === form.has_allergy) return
        form.has_allergy = next
        saveField('has_allergy', next)
    },
})

const actions = computed(() => {
    if (!intervention.value) return []
    if (intervention.value.status === InterventionStatus.inProgress) {
        return [[
            { label: 'Complete', icon: 'i-ph-check-circle-fill', iconClass: 'text-emerald-500', click: () => openStatusChange(InterventionStatus.completed) },
            { label: 'Refused Care', icon: 'i-ph-prohibit-fill', iconClass: 'text-red-400', click: () => openStatusChange(InterventionStatus.refusedCare) },
        ]]
    }
    return [[
        { label: 'Reopen', icon: 'i-ph-arrow-counter-clockwise-fill', click: () => openStatusChange(InterventionStatus.inProgress) },
    ]]
})

function saveError(key: string) {
    return states[key] === 'error' ? 'Not saved.' : undefined
}

function nameOf(id: string) {
    return names.value[id] ?? team.value.find(m => m.id === id)?.name ?? '–'
}

type PickKey = 'area' | 'reason_id' | 'disposition_id' | 'nurse_id' | 'physician_id'

function pick(key: PickKey, value: string | null) {
    const next = value ?? null
    if (intervention.value) {
        if (next === intervention.value[key]) return
        intervention.value[key] = next
    }
    form[key] = next
    saveField(key, next)
}

type TextKey = 'allergy_details' | 'circumstances' | 'clinical_exam' | 'precautions' | 'progress_notes' | 'emergency_contact_name' | 'emergency_contact_phone'

function saveText(key: TextKey) {
    const value = form[key].trim() || null
    if (value === ((intervention.value?.[key] as string | null) ?? null)) return
    if (intervention.value) (intervention.value[key] as string | null) = value
    saveField(key, value)
}

function saveVitals(key: 'vitals_initial' | 'vitals_post', vitals: Vitals) {
    form[key] = vitals
    saveField(key, vitals)
}

function saveDischarge() {
    saveField('discharged_at', fromLocalInput(form.discharged_local))
}

function dischargeNow() {
    form.discharged_local = toLocalInput(new Date().toISOString())
    saveDischarge()
}

useHead({ title: computed(() => patientName.value || 'Intervention') })

onMounted(() => fetchData())

async function fetchData() {
    const [interventionRes, patientRes, optionsRes, membersRes] = await Promise.all([
        client.from('interventions').select('*, post:field_posts (id, name, areas)').eq('id', interventionId).single(),
        client.rpc('get_intervention_patients', { p_intervention_ids: [interventionId] } as never),
        client.from('options').select('*')
            .in('namespace', [OptionNamespace.interventionReason, OptionNamespace.interventionDisposition, OptionNamespace.treatment])
            .eq('is_archived', false).order('label'),
        client.from('field_post_members').select('user:users (id, name)').eq('post_id', postId),
    ])

    if (!interventionRes.data) {
        await navigateTo(`/field-posts/${postId}`, { replace: true })
        return
    }

    const loaded = interventionRes.data as unknown as Intervention
    intervention.value = loaded
    patient.value = ((patientRes.data ?? []) as FieldPostPatient[])[0]
    options.value = (optionsRes.data ?? []) as Option[]

    const members = ((membersRes.data ?? []) as { user: TeamMember | null }[]).map(m => m.user).filter((u): u is TeamMember => !!u)
    const extraIds = [loaded.nurse_id, loaded.physician_id, auth.user?.id].filter((id): id is string => !!id && !members.some(m => m.id === id))
    const lookupIds = [...new Set([...extraIds, loaded.created_by].filter((id): id is string => !!id && !members.some(m => m.id === id)))]
    const found: TeamMember[] = []
    if (lookupIds.length) {
        const { data } = await client.from('users').select('id, name').in('id', lookupIds)
        found.push(...((data ?? []) as TeamMember[]))
    }
    members.push(...found.filter(u => extraIds.includes(u.id)))
    team.value = members.sort((a, b) => a.name.localeCompare(b.name))
    names.value = Object.fromEntries([...members, ...found].map(u => [u.id, u.name]))

    Object.assign(form, {
        area: loaded.area,
        reason_id: loaded.reason_id,
        has_allergy: loaded.has_allergy,
        allergy_details: loaded.allergy_details ?? '',
        circumstances: loaded.circumstances ?? '',
        vitals_initial: loaded.vitals_initial ?? {},
        vitals_post: loaded.vitals_post ?? {},
        clinical_exam: loaded.clinical_exam ?? '',
        precautions: loaded.precautions ?? '',
        disposition_id: loaded.disposition_id,
        discharged_local: toLocalInput(loaded.discharged_at),
        progress_notes: loaded.progress_notes ?? '',
        emergency_contact_name: loaded.emergency_contact_name ?? '',
        emergency_contact_phone: loaded.emergency_contact_phone ?? '',
        nurse_id: loaded.nurse_id,
        physician_id: loaded.physician_id,
    })
}

function onPatientSaved(saved: FieldPostPatient) {
    patient.value = { ...saved, intervention_id: interventionId }
}

function missingLabels(status: InterventionStatus) {
    return missingForCompletion({
        reason_id: form.reason_id,
        disposition_id: form.disposition_id,
        discharged_at: fromLocalInput(form.discharged_local),
        nurse_id: form.nurse_id,
        physician_id: form.physician_id,
    }, status).map(m => MISSING_LABELS[m] ?? m)
}

function openStatusChange(status: InterventionStatus) {
    Object.assign(statusChange, { open: true, loading: false, status, missing: missingLabels(status) })
}

async function confirmStatus() {
    const status = statusChange.status
    statusChange.loading = true
    await flush()

    if (hasErrors.value) {
        statusChange.loading = false
        toast.add({
            title: 'Some changes were not saved.',
            description: 'Retry them before changing the status.',
            color: 'red',
            icon: 'i-ph-x-circle-duotone',
            actions: [{ label: 'Retry', color: 'white', click: () => retryAll() }],
        })
        return
    }

    statusChange.missing = missingLabels(status)
    if (statusChange.missing.length) {
        statusChange.loading = false
        return
    }

    const { error } = await client.rpc('complete_intervention', { p_id: interventionId, p_status: status } as never)
    statusChange.loading = false

    if (error) {
        if (error.message === 'INTERVENTION_INCOMPLETE') {
            statusChange.missing = (error.details ?? '').split(',').filter(Boolean).map(f => MISSING_LABELS[f] ?? f)
            return
        }
        toast.add({ title: 'Could not update the status.', description: error.message, color: 'red', icon: 'i-ph-x-circle-duotone' })
        return
    }

    toast.add({ title: `Intervention marked ${INTERVENTION_STATUS_META[status].label}.`, color: 'green', icon: 'i-ph-check-circle-duotone' })
    if (intervention.value) intervention.value.status = status
    statusChange.open = false
}
</script>
